import { useEffect, useState } from 'react';
import { reportApi } from '../utils/api';
import { ScrollText, Loader, RefreshCw, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const ACTION_LABELS = {
  user_created: { label: 'Usuario creado', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  vacation_approved: { label: 'Vacaciones aprobadas', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  vacation_rejected: { label: 'Vacaciones rechazadas', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  vacation_cancelled: { label: 'Vacaciones canceladas', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800/30 dark:text-gray-400' },
  vacation_created: { label: 'Vacaciones solicitadas', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  vacation_requested: { label: 'Vacaciones solicitadas', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  remote_approved: { label: 'Teletrabajo aprobado', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  remote_rejected: { label: 'Teletrabajo rechazado', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  remote_created: { label: 'Teletrabajo solicitado', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  remote_work_requested: { label: 'Teletrabajo solicitado', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  remote_work_approved: { label: 'Teletrabajo aprobado', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  remote_work_rejected: { label: 'Teletrabajo rechazado', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  remote_work_cancelled: { label: 'Teletrabajo cancelado', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800/30 dark:text-gray-400' },
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await reportApi.auditLogs();
      setLogs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = logs.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      l.performed_by_name?.toLowerCase().includes(q) ||
      l.action?.toLowerCase().includes(q) ||
      l.entity_type?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-brand" />
            Log de Auditoría
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Registro de todas las acciones realizadas en la plataforma
          </p>
        </div>
        <Button variant="outline" onClick={loadLogs} className="gap-2" data-testid="btn-refresh-audit">
          <RefreshCw className="h-4 w-4" />
          Actualizar
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8"
            data-testid="audit-search"
          />
        </div>
        <span className="text-xs text-muted-foreground">{filtered.length} registros</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
          <ScrollText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Sin registros de auditoría</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-4 gap-4 px-4 py-2.5 bg-muted/50 border-b border-border text-xs font-semibold text-muted-foreground">
            <span className="col-span-1">FECHA / HORA</span>
            <span className="col-span-1">ACCIÓN</span>
            <span className="col-span-1">REALIZADO POR</span>
            <span className="col-span-1">DETALLES</span>
          </div>
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {filtered.map(log => {
              const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-600' };
              return (
                <div
                  key={log.id}
                  className="grid grid-cols-4 gap-4 px-4 py-3 hover:bg-muted/30 transition-colors items-start"
                  data-testid={`audit-row-${log.id}`}
                >
                  <div className="text-xs text-muted-foreground">
                    {new Date(log.created_at).toLocaleString('es-ES', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </div>
                  <div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionInfo.color}`}>
                      {actionInfo.label}
                    </span>
                  </div>
                  <div className="text-sm font-medium truncate">
                    {log.performed_by_name || log.performed_by}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {Object.entries(log.details || {}).map(([k, v]) => (
                      <span key={k} className="mr-2">
                        <span className="font-medium">{k}:</span> {String(v)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
